import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { Alert, Box, CircularProgress, Grid, IconButton, InputAdornment, Link, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';

// import { useSnackbar } from "notistack";

import { DataAPIContext, GET_ONE } from '../api';
import type { LegacyKeyStatus } from '../developer/legacyImportKey';
import { ContextStore } from '../store/contextStore';
import { copyTextToClipboard } from '../utils';

type ApiKeys = {
    import_source_key: string | null;
    import_access_token: string | null;
};

const LOAD_ERROR = 'Could not load your API keys. Please reload the page or contact support@curatedplanet.com.';

// Rendered inside the Developer section's page (developer/ApiKeysPage), which gives it its title and
// margins; it no longer fills a fixed 85vh box, so the API keys table can follow it.
const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        overflowX: 'hidden',
    },
}));

type Props = {
    /** Whether the access key (the Import_Access_Token UUID) can authenticate (developer/legacyImportKey):
     *  `refused` hides it, `pending` waits. Without it the key is shown, as before. */
    accessKeyStatus?: (token: string) => LegacyKeyStatus;
    /** The page offers New key (`api_credential.write`): a hidden or missing access key points there. */
    newKeyAvailable?: boolean;
};

const ImportApiKeys = ({ accessKeyStatus, newKeyAvailable = false }: Props) => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const { tourOperator } = React.useContext(ContextStore);
    const { enqueueSnackbar } = useSnackbar();
    const [keys, setKeys] = React.useState<ApiKeys | null>(null);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    // A 403's words ("not for you", e.g. developer.keys_manage without import.list + import.dev): plain text.
    const [forbidden, setForbidden] = React.useState<string | null>(null);

    // Fetched here, not read from the login payload in `tourOperator`: the settings pages
    // replace that cached copy with /tour_operator/{id}/ responses, where both keys are null.
    // A 403 only means "not for you" and must not sign the user out.
    React.useEffect(() => {
        let active = true;
        dataAPI(GET_ONE, 'tour_operator', { urlSuffix: 'api_keys/', keepSessionOn403: true })
            .then((res: any) => {
                if (!active) {
                    return;
                }
                if (res.statusCode === 200) {
                    setKeys(res.data);
                } else if (res.statusCode === 404) {
                    // A backend without the endpoint yet: the login payload is all there is.
                    setKeys({ import_source_key: tourOperator?.import_source_key, import_access_token: tourOperator?.import_access_token });
                } else if (res.statusCode === 403) {
                    setForbidden(res.data?.message || res.data?.detail || 'You do not have permission to view the import API keys');
                } else {
                    setLoadError(res.data?.message || LOAD_ERROR);
                }
            })
            .catch(() => {
                if (active) {
                    setLoadError(LOAD_ERROR);
                }
            });
        return () => {
            active = false;
        };
        // Not `tourOperator`: it is a fresh object on every bootstrap write (the my_permissions
        // poll), which would refetch in a loop. Only the 404 fallback reads it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAPI]);

    const copyToClipboard = (text: string | null, label: string) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };

    if (!keys) {
        return (
            <div className={classes.root}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12} mt={2}>
                        {forbidden ? (
                            <Typography variant="body1">{forbidden}</Typography>
                        ) : loadError ? <Alert severity="error">{loadError}</Alert> : <CircularProgress size={24} />}
                    </Grid>
                </Grid>
            </div>
        );
    }

    const httpHeader = `Authorization: Bearer ${keys.import_access_token}`;
    const accessKey = keys.import_access_token ? (accessKeyStatus ? accessKeyStatus(keys.import_access_token) : 'works') : null;
    // No key (the backend answers null for one that does not authenticate) or a refused one: point to New key.
    const showNewKeyHint = newKeyAvailable && (!accessKey || accessKey === 'refused');

    return (
        <div className={classes.root}>
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12} mt={2}>
                    <Typography variant="h6">XML import key</Typography>
                    {keys.import_source_key ? (
                        <>
                            <Typography variant="body1" component="div">
                                <Box sx={{ my: 2 }}>
                                    This is the key that you should put into your XML files as <strong>source_id</strong> for activities, tours, itineraries and users.
                                </Box>
                                <Box sx={{ my: 2 }}>
                                    <TextField
                                        disabled
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        style={{ maxWidth: '36em' }}
                                        value={keys.import_source_key}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => copyToClipboard(keys.import_source_key, 'Import source key')}>
                                                        <FileCopyOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        helperText="Your XML import key"
                                    />
                                </Box>
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="body1" mb={4}>
                                XML import key is not configured for you. Please request it from <Link href="mailto:support@curatedplanet.com">support@curatedplanet.com</Link>.
                            </Typography>
                        </>
                    )}
                    <Typography variant="h6">Import API access key</Typography>
                    {accessKey === 'pending' ? (
                        <Box sx={{ my: 2 }}><CircularProgress size={20} /></Box>
                    ) : showNewKeyHint ? (
                        <Typography variant="body1" component="div" sx={{ my: 2 }} data-testid="import-access-key-new">
                            To make HTTP requests to Curated Planet import API, create a key with <strong>New key</strong> and
                            use it in the HTTP Authorization header: <code>Authorization: Bearer &lt;your key&gt;</code>.
                        </Typography>
                    ) : accessKey && accessKey !== 'refused' ? (
                        <>
                            <Typography variant="body1" component="div">
                                <Box sx={{ my: 2 }}>This is the key that you use to make HTTP requests to Curated Planet import API. Please use HTTP Authorization header as shown below.</Box>
                                <Box sx={{ my: 2 }}>
                                    <TextField
                                        disabled
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        style={{ maxWidth: '36em' }}
                                        value={keys.import_access_token}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => copyToClipboard(keys.import_access_token, 'Import API access key')}>
                                                        <FileCopyOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        helperText="Your import API key"
                                    />
                                </Box>
                                <Box sx={{ my: 2 }}>
                                    <TextField
                                        disabled
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        style={{ maxWidth: '36em' }}
                                        value={httpHeader}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => copyToClipboard(httpHeader, 'HTTP Authorization header')}>
                                                        <FileCopyOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        helperText="HTTP Authorization header"
                                    />
                                </Box>
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="body1" component="div">
                                Import API access key is not configured for you. Please request it from <Link href="mailto:support@curatedplanet.com">support@curatedplanet.com</Link>.
                            </Typography>
                        </>
                    )}
                </Grid>
            </Grid>
        </div>
    );
};

export default ImportApiKeys;
